// Vocabulaire du modèle de tarification, isolé dans son propre fichier pour
// éviter un cycle d'imports : pricingModel.ts a besoin de readRuleSnapshot
// (défini dans ruleSnapshot.ts), et ruleSnapshot.ts a besoin de
// normalizePricingModel. Ce fichier n'importe rien : il peut être importé par
// les deux sans créer de boucle.

/** Modèle de tarification d'un type de compte prop firm.
 *  - one_time      : challenge payé une fois (historique, défaut de la colonne) ;
 *  - subscription  : challenge payé au mois, jusqu'au passage en funded ;
 *  - direct_funded : compte funded dès l'achat, aucune phase de challenge. */
export const PRICING_MODELS = ["one_time", "subscription", "direct_funded"] as const;
export type PricingModel = (typeof PRICING_MODELS)[number];

export function normalizePricingModel(raw: unknown): PricingModel {
  return (PRICING_MODELS as readonly unknown[]).includes(raw) ? (raw as PricingModel) : "one_time";
}

export const isDirectFunded = (raw: unknown) => normalizePricingModel(raw) === "direct_funded";
export const isSubscription = (raw: unknown) => normalizePricingModel(raw) === "subscription";

/** Un type de phase qui suppose un challenge : absent d'un compte direct funded. */
function isEvaluationPhaseType(phaseType: string): boolean {
  return phaseType === "evaluation" || phaseType === "evaluation_2";
}

/**
 * Une phase d'evaluation a-t-elle un sens sur ce type de compte ?
 *
 * PIEGE CORRIGE ICI (revue Task 12) : la premiere version de ce garde
 * rejetait TOUTE ecriture de phase evaluation sur un type direct funded, y
 * compris la simple MISE A JOUR d'une phase evaluation deja en base qui GARDE
 * son type. Or `PhaseFormFields` la laisse deliberement editable (voir
 * `selectOptions` dans PhaseEditor.tsx) pour le cas ou le type de compte a ete
 * bascule en direct funded APRES coup : l'admin doit pouvoir corriger son nom
 * ou ses regles sans que le serveur la lui refuse.
 *
 * La regle correcte porte donc sur le CHANGEMENT, pas sur l'etat : on bloque
 * seulement
 *   (a) la CREATION d'une phase evaluation (`existingPhaseType` absent), et
 *   (b) une mise a jour qui FAIT ENTRER une phase dans le type evaluation
 *       (`existingPhaseType` n'etait pas evaluation, `nextPhaseType` l'est).
 * Une mise a jour qui GARDE son type evaluation (ou en sort) est autorisee.
 *
 * @param existingPhaseType Le type STOCKE de la phase avant l'ecriture.
 *   `null`/`undefined` pour une phase neuve (pas encore d'id).
 */
export function rejectsEvaluationPhaseWrite(
  pricingModel: unknown,
  nextPhaseType: string,
  existingPhaseType: string | null | undefined,
): boolean {
  if (!isDirectFunded(pricingModel)) return false;
  if (!isEvaluationPhaseType(nextPhaseType)) return false;
  // Creation directe en evaluation : toujours refusee.
  if (existingPhaseType == null) return true;
  // Mise a jour : refusee seulement si le type EXISTANT n'etait pas deja
  // evaluation (sinon l'admin ne fait que garder/editer une phase existante).
  return !isEvaluationPhaseType(existingPhaseType);
}
